/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CollectionJobStatus.tsx
 *     Feature         :   139-collection-job-console
 *     Description     :   Status panel — idle vs running with live progress (FR-010, FR-011)
 *     Constitution    :   v1.12.0 | Principle XXXVII (Code Headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-03-02
 * =========================================================================================
 */

import React from 'react';
import { Activity, CheckCircle2, AlertTriangle, Clock, Zap } from 'lucide-react';
import type { CollectionJobStatus as StatusType } from '../../types/collectionJob';
import { formatEastern } from '@/utils/dateFormatter';

interface CollectionJobStatusProps {
  status: StatusType | undefined;
  isLoading: boolean;
}

/** Format seconds into "Xh Ym Zs" display */
function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** Format an ISO date string to locale-friendly form */
function formatDate(iso: string): string {
  return formatEastern(iso);
}

/** Two-hour threshold for stale job warning (FR-011) */
const STALE_THRESHOLD_SECONDS = 2 * 3600;

export const CollectionJobStatus: React.FC<CollectionJobStatusProps> = ({ status, isLoading }) => {
  if (isLoading || !status) {
    return (
      <div className="bg-[var(--color-bg-card)] rounded-xl border border-[var(--color-border)] p-6 animate-pulse">
        <div className="h-6 bg-[var(--color-bg-subtle)] rounded w-48 mb-4" />
        <div className="h-4 bg-[var(--color-bg-subtle)] rounded w-32" />
      </div>
    );
  }

  if (status.state === 'running') {
    const isStale = (status.elapsed_seconds ?? 0) > STALE_THRESHOLD_SECONDS;
    const progressPct = status.total_steps && status.step_index != null
      ? Math.min(100, Math.round((status.step_index / status.total_steps) * 100))
      : 0;

    return (
      <div className={`bg-[var(--color-bg-card)] rounded-xl border p-6 ${isStale ? 'border-amber-400 dark:border-amber-500' : 'border-blue-300 dark:border-blue-600'}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500" />
            </span>
            <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">Running</span>
          </div>
          {isStale && (
            <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium">Running &gt;2 hours</span>
            </div>
          )}
        </div>

        {/* Current step */}
        {status.current_step && (
          <p className="text-sm text-[var(--color-text-secondary)] mb-2">
            <span className="font-medium">Step:</span> {status.current_step}
            {status.step_index != null && status.total_steps && (
              <span className="text-[var(--color-text-secondary)]"> ({status.step_index}/{status.total_steps})</span>
            )}
          </p>
        )}

        {/* Progress bar */}
        <div className="w-full bg-[var(--color-bg-subtle)] rounded-full h-2.5 mb-3">
          <div
            className="bg-blue-500 h-2.5 rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 text-xs text-[var(--color-text-secondary)]">
          <div className="flex items-center gap-1">
            <Zap className="w-3.5 h-3.5" />
            {/* Spec 298 FR-006 (row 11): undefined when fcjlogs has no active run or its read fails. */}
            <span>
              {status.entities_collected != null
                ? `${status.entities_collected.toLocaleString()} entities`
                : 'entities not reported'}
            </span>
          </div>
          {(status.errors_count ?? 0) > 0 && (
            <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>{status.errors_count} errors</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{formatElapsed(status.elapsed_seconds ?? 0)}</span>
          </div>
        </div>
      </div>
    );
  }

  // Idle state
  return (
    <div className="bg-[var(--color-bg-card)] rounded-xl border border-[var(--color-border)] p-6">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 className="w-4 h-4 text-green-500" />
        <span className="text-sm font-semibold text-[var(--color-text-secondary)]">No job running</span>
      </div>
      {status.last_run ? (
        <div className="text-xs text-[var(--color-text-secondary)] space-y-1">
          <p>
            <span className="font-medium">Last run:</span>{' '}
            {formatDate(status.last_run.completed_at)} — {status.last_run.status}
          </p>
          <p>
            <span className="font-medium">Entities:</span> {status.last_run.total_entities.toLocaleString()}
            {' · '}
            <span className="font-medium">Duration:</span> {formatElapsed(status.last_run.duration_seconds)}
          </p>
        </div>
      ) : (
        <p className="text-xs text-[var(--color-text-secondary)]">No previous runs found</p>
      )}
    </div>
  );
};
